/**
 * React Query hooks — Applications (list, detail, stage moves).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
	RecruitmentApiService,
	type ApplicationFilters,
	type MoveStageBody,
} from '../../../services/recruitmentApi';

const KEY = ['recruitment', 'applications'] as const;

export const useApplications = (filters: ApplicationFilters = {}) =>
	useQuery({
		queryKey: [...KEY, 'list', filters],
		queryFn: async () => {
			const resp = await RecruitmentApiService.listApplications(filters);
			return resp.data; // { page, page_size, total, rows }
		},
	});

export const useApplication = (id: number | undefined) =>
	useQuery({
		queryKey: [...KEY, 'detail', id],
		enabled: typeof id === 'number' && !Number.isNaN(id),
		queryFn: async () => {
			const resp = await RecruitmentApiService.getApplication(id as number);
			return resp.data; // { application, stage_history }
		},
	});

export const useMoveStage = () => {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: async ({ id, body }: { id: number; body: MoveStageBody }) => {
			const resp = await RecruitmentApiService.moveStage(id, body);
			return resp.data; // { application, stage_history }
		},
		onSuccess: (_data, variables) => {
			qc.invalidateQueries({ queryKey: KEY });
			qc.invalidateQueries({ queryKey: [...KEY, 'detail', variables.id] });
		},
	});
};
